import React, { useEffect, useState } from 'react';
import {
  Send, ArrowRight, CheckCircle2, Clock3, ShieldCheck, Truck, LockKeyhole,
  TrendingUp, Users, MapPin, ChevronRight, FileCheck2,
} from 'lucide-react';
import { useTxStore, useUIStore, useAuthStore } from '../../store';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import mapImg from '../../assets/cuba-network.webp';
import { HOTSPOTS } from '../../components/shared/CubaNetworkMap';

const ACTIVE_STATUSES = ['in_delivery', 'queued_offline', 'pending_payment', 'payment_received', 'kyc_review'];

const STATUS_MAP = {
  completed:        { cls: 'done',    label: 'Entregado' },
  in_delivery:      { cls: 'moving',  label: 'En camino' },
  queued_offline:   { cls: 'waiting', label: 'Cola offline' },
  pending_payment:  { cls: 'waiting', label: 'Pago pendiente' },
  payment_received: { cls: 'moving',  label: 'Pago confirmado' },
  kyc_review:       { cls: 'waiting', label: 'En revisión' },
  failed:           { cls: 'failed',  label: 'Fallido' },
  refunded:         { cls: 'waiting', label: 'Reembolsado' },
};

const PROVINCE_POS = {
  'Pinar del Río':       { x: 88,  y: 70 },
  'Artemisa':            { x: 136, y: 49 },
  'La Habana':           { x: 156, y: 34 },
  'Mayabeque':           { x: 168, y: 42 },
  'Matanzas':            { x: 197, y: 38 },
  'Villa Clara':         { x: 280, y: 71 },
  'Cienfuegos':          { x: 256, y: 85 },
  'Sancti Spíritus':     { x: 307, y: 97 },
  'Ciego de Ávila':      { x: 342, y: 102 },
  'Camagüey':            { x: 385, y: 128 },
  'Las Tunas':           { x: 435, y: 151 },
  'Holguín':             { x: 471, y: 155 },
  'Granma':              { x: 451, y: 183 },
  'Santiago de Cuba':    { x: 493, y: 203, a: 'end' },
  'Guantánamo':          { x: 525, y: 196, a: 'end' },
  'Isla de la Juventud': { x: 134, y: 100 },
};

const fmt = n => new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(n);

function Status({ status }) {
  const s = STATUS_MAP[status] || { cls: 'waiting', label: status };
  return <span className={`g-status ${s.cls}`}><span className="dot" />{s.label}</span>;
}

function Avatar({ name, size = 38 }) {
  const initials = (name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#ffd9c4,#ff9a6c)',
      display: 'grid', placeItems: 'center',
      fontSize: size * 0.33, fontWeight: 800, color: '#86301a',
    }}>{initials}</div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `Hace ${Math.max(1, Math.round(diff / 60))} min`;
  if (diff < 86400) return `Hace ${Math.round(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

function useQuote(amount, currency) {
  const [q, setQ] = useState(null);
  useEffect(() => {
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) { setQ(null); return; }
    const t = setTimeout(() => {
      api.get('/api/v1/quote/', { params: { amount: parsed, currency } })
        .then(r => setQ(r.data))
        .catch(() => setQ(null));
    }, 400);
    return () => clearTimeout(t);
  }, [amount, currency]);
  return q;
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const { transactions, fetch, loading } = useTxStore();
  const { openTxDetail } = useUIStore();
  const navigate = useNavigate();
  const openSendModal = () => navigate('/app/enviar');
  const [agents, setAgents] = useState([]);
  const [amount, setAmount] = useState('100');
  const [currency, setCurrency] = useState('USD');
  const quote = useQuote(amount, currency);

  useEffect(() => { fetch(); }, []);
  useEffect(() => {
    api.get('/api/v1/marketplace/agents')
      .then(r => setAgents(Array.isArray(r.data) ? r.data : (r.data?.agents || [])))
      .catch(() => setAgents([]));
  }, []);

  const now = new Date();
  const thisMonth = t => {
    const d = t.created_at ? new Date(t.created_at) : null;
    return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  };
  const completed  = transactions.filter(t => t.status === 'completed');
  const active     = transactions.filter(t => ACTIVE_STATUSES.includes(t.status));
  const failed     = transactions.filter(t => t.status === 'failed');
  const monthTxs   = transactions.filter(thisMonth);
  const monthTotal = monthTxs.reduce((s, t) => s + (parseFloat(t.amount_usd) || 0), 0);
  const successPct = (completed.length + failed.length) > 0
    ? Math.round(100 * completed.length / (completed.length + failed.length)) : null;

  const byProvince = {};
  agents.forEach(a => { const p = a.province || 'Otra'; byProvince[p] = (byProvince[p] || 0) + 1; });
  const provinces = Object.keys(byProvince);

  const recent = [...transactions]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 5);

  const cupOut = quote?.cup_to_deliver ?? quote?.cup ?? null;
  const rateLine = quote?.rate_cup ?? quote?.rate ?? null;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Hero + cotizador */}
      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 18, alignItems: 'stretch' }}>
        <section className="g-card" style={{
          padding: '30px 32px',
          background: 'linear-gradient(115deg, #fff8f5, #fff2e8), #fff',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div className="g-eyebrow">✦ Remesas con protección escrow</div>
          <h1 style={{ fontSize: 'clamp(30px, 3.4vw, 46px)', lineHeight: 1.06, letterSpacing: '-1.5px', margin: '12px 0' }}>
            Dinero en mano<br />en toda <em style={{ fontStyle: 'normal', color: 'var(--orange)' }}>Cuba.</em>
          </h1>
          <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 520, margin: '0 0 18px' }}>
            Bienvenido{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}. Tú pagas desde el exterior,
            un agente verificado entrega CUP a la puerta del destinatario. El pago al agente se libera
            después de verificar la entrega.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <button className="g-primary" onClick={openSendModal}>Enviar dinero <ArrowRight size={17} /></button>
          </div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12.5, fontWeight: 600, color: '#6c3224' }}>
            <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}><ShieldCheck size={16} /> Agentes verificados</span>
            <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}><Truck size={16} /> Puerta a puerta</span>
            <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}><LockKeyhole size={16} /> Escrow por remesa</span>
          </div>
        </section>

        <section className="g-card" style={{ padding: 22 }}>
          <div className="g-section-title">
            <h2>Enviar a Cuba</h2>
            <span className="g-eyebrow">Cotizador</span>
          </div>
          <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--faint)' }}>Tú pagas</label>
          <div style={{ display: 'flex', gap: 8, margin: '6px 0 14px' }}>
            <input type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)}
              style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--card-border)', fontSize: 19, fontWeight: 700, outline: 'none' }} />
            <select value={currency} onChange={e => setCurrency(e.target.value)}
              style={{ padding: '0 12px', borderRadius: 12, border: '1px solid var(--card-border)', fontWeight: 700, background: '#fff' }}>
              <option>USD</option><option>EUR</option><option>BRL</option>
            </select>
          </div>
          <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--faint)' }}>El destinatario recibe (CUP)</label>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 14px', borderRadius: 12, background: 'var(--bg)',
            border: '1px solid var(--card-border)', margin: '6px 0 12px',
          }}>
            <strong style={{ fontSize: 21 }}>{cupOut !== null ? fmt(cupOut) : '…'}</strong>
            <span style={{ fontSize: 13, fontWeight: 700 }}>🇨🇺 CUP</span>
          </div>
          {rateLine !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--orange)', fontWeight: 700, marginBottom: 14 }}>
              <TrendingUp size={16} /> Tasa actual: 1 {currency} = {fmt(rateLine)} CUP
            </div>
          )}
          <button className="g-primary" style={{ width: '100%' }} onClick={openSendModal}>
            Continuar <ArrowRight size={17} />
          </button>
        </section>
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        {[
          { Icon: Send,         tint: '#fef0e9', color: '#e74a20', label: 'ENVIADO ESTE MES', value: `$${fmt(monthTotal)}`, sub: `${monthTxs.length} transacciones` },
          { Icon: CheckCircle2, tint: '#eaf6ee', color: '#1e7a45', label: 'ENTREGAS COMPLETADAS', value: completed.length, sub: 'con prueba de entrega' },
          { Icon: Clock3,       tint: '#fef0e9', color: '#e74a20', label: 'EN PROCESO', value: active.length, sub: active.length ? 'en curso ahora' : 'ninguna activa' },
          { Icon: ShieldCheck,  tint: '#eaf6ee', color: '#1e7a45', label: 'TASA DE ÉXITO', value: successPct === null ? '—' : `${successPct}%`, sub: 'entregas confirmadas' },
        ].map(({ Icon, tint, color, label, value, sub }, i) => (
          <div key={i} className="g-card" style={{ padding: 18, display: 'flex', gap: 14, alignItems: 'center' }}>
            <span style={{ width: 46, height: 46, borderRadius: 13, flexShrink: 0, background: tint, color, display: 'grid', placeItems: 'center' }}>
              <Icon size={21} />
            </span>
            <div>
              <small style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.07em', color: 'var(--faint)' }}>{label}</small>
              <h2 style={{ fontSize: 24, margin: '2px 0', lineHeight: 1.1 }}>{value}</h2>
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recientes + cobertura */}
      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 14 }}>
        <section className="g-card" style={{ padding: 22 }}>
          <div className="g-section-title">
            <h2>Transacciones recientes</h2>
            <a className="g-text-link" href="/app/historial">Ver todas <ArrowRight size={15} /></a>
          </div>
          {loading && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Cargando…</p>}
          {!loading && recent.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>
              Aún no tienes transacciones.{' '}
              <button className="g-text-link" onClick={openSendModal}>Envía tu primera remesa <ArrowRight size={14} /></button>
            </p>
          )}
          {recent.map((tx, i) => (
            <button key={tx.ref || i} onClick={() => openTxDetail(tx.ref)} style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%',
              padding: '12px 2px', background: 'none', border: 'none',
              borderTop: i > 0 ? '1px solid #f1f1f4' : 'none',
              cursor: 'pointer', textAlign: 'left', font: 'inherit',
            }}>
              <Avatar name={tx.recipient_name} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <b style={{ fontSize: 13.5, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tx.recipient_name || 'Destinatario'}
                </b>
                <small style={{ fontSize: 11, color: 'var(--faint)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <MapPin size={11} />{tx.recipient_province || tx.recipient_phone || tx.ref}
                </small>
              </span>
              <span style={{ textAlign: 'right' }}>
                <b style={{ fontSize: 13.5, display: 'block' }}>${fmt(parseFloat(tx.amount_usd || 0))}</b>
                {tx.cup_to_deliver && <small style={{ fontSize: 11, color: 'var(--faint)' }}>{fmt(parseFloat(tx.cup_to_deliver))} CUP</small>}
              </span>
              <Status status={tx.status} />
              <ChevronRight size={16} color="#9c9daa" />
            </button>
          ))}
        </section>

        <section className="g-card" style={{ padding: 22 }}>
          <div className="g-section-title">
            <h2>Cobertura en Cuba</h2>
            <a className="g-text-link" href="/app/agentes">Ver agentes <ArrowRight size={15} /></a>
          </div>
          <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#05120f', aspectRatio: '1860 / 700' }}>
            <img src={mapImg} alt="Cobertura de agentes en Cuba"
              style={{ width: '100%', display: 'block' }} loading="lazy" />
            {HOTSPOTS.filter(p => byProvince[p.name]).map(p => (
              <span key={p.name} style={{
                position: 'absolute', left: `${p.x}%`, top: `${p.y * 845 / 700}%`,
                transform: 'translate(-50%, 55%)',
                background: '#0b1b15', border: '1.5px solid #4ade80', color: '#4ade80',
                borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 800,
              }}>{byProvince[p.name]}</span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            {[
              { Icon: Users, v: agents.length, l: 'Agentes verificados' },
              { Icon: MapPin, v: provinces.length, l: 'Provincias activas' },
            ].map(({ Icon, v, l }, i) => (
              <div key={i} style={{ flex: 1, background: 'var(--bg)', borderRadius: 13, padding: '11px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
                <Icon size={18} color="#e74a20" />
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.1 }}>{v}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>{l}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Cómo funciona */}
      <section className="g-card" style={{ padding: 22 }}>
        <div className="g-section-title"><h2>Cómo funciona</h2></div>
        <div className="grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
          {[
            ['01', Send,        'Tú pagas', 'Con tarjeta, transferencia o cripto. Se abre el escrow.'],
            ['02', Truck,       'Un agente entrega', 'Un agente verificado lleva CUP a la puerta.'],
            ['03', FileCheck2,  'Prueba de entrega', 'El agente sube foto y confirmación de la entrega.'],
            ['04', ShieldCheck, 'Se libera el pago', 'Solo entonces se libera el pago al agente en USDT.'],
          ].map(([num, Icon, title, desc]) => (
            <div key={num} style={{ display: 'flex', gap: 12 }}>
              <span style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: 'var(--orange-grad)', color: '#fff',
                display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800,
              }}>{num}</span>
              <div>
                <b style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={15} color="#e74a20" /> {title}</b>
                <small style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, display: 'block', marginTop: 3 }}>{desc}</small>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
